// Color constants mirroring SCSS variables for TypeScript usage
// Keep in sync with src/styles/variables.scss

export const colors = {
  // Primary - Teal (locked, do not change)
  primary: '#14b8a6',
  primaryDark: '#0d9488',
  primaryLight: '#5eead4',

  // Secondary - Cyan (locked, do not change)
  secondary: '#0891b2',
  secondaryDark: '#0e7490',
  secondaryLight: '#22d3ee',

  // Accent (locked)
  accent: '#06b6d4',

  // Semantic - Error
  error: '#ef4444',
  errorDark: '#dc2626',
  errorLight: '#fecaca',

  // Semantic - Success
  success: '#10b981',
  successDark: '#059669',
  successLight: '#d1fae5',

  // Semantic - Warning
  warning: '#f59e0b',
  warningDark: '#d97706',
  warningLight: '#fef3c7',

  // Semantic - Info (cyan-teal instead of blue)
  info: '#0891b2',
  infoDark: '#0e7490',
  infoLight: '#cffafe',

  // Semantic background tints (lighter than *-light; for section/chip bg fills)
  errorBg: '#fef2f2',
  warningBg: '#fef3c7',
  successBg: '#f0fdf4',
  infoBg: 'rgba(8, 145, 178, 0.05)',

  // Text — deeper contrast for legibility (was #1e293b → #0f172a)
  textPrimary: '#0f172a',
  textSecondary: '#475569', // was #64748b — deeper for hierarchy
  textMuted: '#94a3b8',

  // Background — exact mockup token table values
  background: '#dce3e8',
  backgroundPaper: '#ffffff',
  backgroundRecessed: '#e8eef2', // cool teal-gray panels — mid-tier

  // Border — softer than before
  border: '#e2e8f0',
  borderSoft: 'rgba(15, 23, 42, 0.06)', // new: subtle borders
  borderStrong: 'rgba(15, 23, 42, 0.12)', // new: emphasized borders

  // Neutral (Slate scale) — unchanged
  slate50: '#f8fafc',
  slate100: '#f1f5f9',
  slate200: '#e2e8f0',
  slate300: '#cbd5e1',
  slate400: '#94a3b8',
  slate500: '#64748b',
  slate600: '#475569',
  slate700: '#334155',
  slate800: '#1e293b',
  slate900: '#0f172a',

  // AI content tokens — new
  aiAccent: '#0d9488',
  aiBackgroundSoft: 'rgba(20, 184, 166, 0.05)',
  aiBorder: 'rgba(20, 184, 166, 0.22)',
  aiHighlightBg: 'rgba(20, 184, 166, 0.10)',

  // Domain-specific: 3D viewer materials (TKR digital twin)
  bone: '#f5e6d3',
  implantMetal: '#5a94d0',
  implantPE: '#e6f1fb',

  // Domain-specific: category accent colors (workflow, education)
  indigo: '#4f46e5',
  indigoLight: '#818cf8',
  violet: '#8b5cf6',
  pink: '#ec4899',

  // Domain-specific: dark status text (severity labels, clinical status)
  successText: '#065f46',
  warningText: '#92400e',
  errorText: '#991b1b',
  orangeText: '#9a3412',
  orange: '#f97316',
  orangeLight: '#ffedd5',
  emerald: '#047857',

  // Domain-specific: SDM agent stream, chart accents
  tealMuted: '#7ec8c0',
  navy: '#0f1e35',
  blueAccent: '#1a5ea8',

  // Domain-specific: clinical joint/PE diagram states
  orangeStroke: '#ea580c',
  rose: '#e11d48',
  roseDark: '#9f1239',
  green600: '#16a34a',

  // Domain-specific: 3D background particles
  teal700: '#0f766e',
  rose700: '#be123c',

  // Domain-specific: header
  headerSlate: '#2d5a75',

  // Domain-specific: session status / misc UI
  gray500: '#6B7280',
  gray400: '#718096',
  greenLight: '#86efac',
  redLight: '#fca5a5',

  // Pedigree chart fill (family history carrier pattern)
  charcoal: '#1f2937',
} as const;

// Gradient presets — primary stays, page gradient retired
export const gradients = {
  primary: 'linear-gradient(to right, #0d9488, #11a89a)',
  primaryHover: 'linear-gradient(to right, #22d3ee, #2dd4bf)',
  // Retired: page (was a cool gradient, replaced with warm flat bg)
  card: 'linear-gradient(135deg, #14b8a6 0%, #0891b2 100%)',
  // New: AI top-edge gradient for cards
  aiTopEdge: 'linear-gradient(90deg, transparent, #14b8a6, #06b6d4, transparent)',
} as const;

// Status colors for sessions/records — unchanged
export const statusColors = {
  completed: { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
  inProgress: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  pending: { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
  cancelled: { bg: '#fecaca', text: '#991b1b', border: '#ef4444' },
} as const;

// Layered shadow tokens — cyan-tinted (replaces hardcoded rgba(0,0,0))
export const shadows = {
  none: 'none',
  sm: '0 1px 4px rgba(8, 145, 178, 0.10), 0 1px 2px rgba(15, 23, 42, 0.05)',
  md: '0 4px 16px -2px rgba(8, 145, 178, 0.22), 0 2px 6px rgba(15, 23, 42, 0.06)',
  lg: '0 8px 24px -4px rgba(8, 145, 178, 0.26), 0 2px 8px rgba(15, 23, 42, 0.06)',
  xl: '0 12px 36px -6px rgba(8, 145, 178, 0.30), 0 4px 10px rgba(15, 23, 42, 0.07)',
  // Used for AI cards
  ai: '0 8px 24px -4px rgba(8, 145, 178, 0.26), 0 2px 8px rgba(15, 23, 42, 0.06)',
} as const;

export type ColorKey = keyof typeof colors;
export type StatusKey = keyof typeof statusColors;
export type ShadowKey = keyof typeof shadows;
